import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  ROLE_ASSIGNMENT_CONFLICT_MESSAGES,
  ROLE_OWNERSHIPS,
  ROLE_OWNERSHIP_LABELS,
  ROLE_SCOPE_KINDS,
  ROLE_SCOPE_KIND_LABELS,
  roleAssignmentConflicts,
  type RoleOwnership,
  type RoleScopeKind,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Field } from '../../../components/Field';
import { MultiSelect, Select } from '../../../components/Select';
import { useToasts } from '../../../stores/toasts';
import { useRoles } from '../api/roles-api';
import {
  useDepartments,
  useSetUserRoles,
  useVentures,
  type UserDetail,
} from '../api/users-api';
import styles from './RoleAssignmentEditor.module.css';

/**
 * F2.04 — role AND scope, never one without the other.
 *
 * This is where S4 layer 2 is enforced at the point a person can see it: the save
 * button stays disabled while any row is a role with no scope, because "Recruiter"
 * on its own is not a permission and an interface that lets somebody submit it is
 * an interface that will produce one.
 */

interface Draft {
  readonly key: string;
  roleId: string | null;
  scopeKind: RoleScopeKind;
  ownership: RoleOwnership;
  ventureIds: string[];
  departmentIds: string[];
}

let nextKey = 0;

export function RoleAssignmentEditor({
  user,
  canEdit,
}: {
  readonly user: UserDetail;
  readonly canEdit: boolean;
}): ReactNode {
  const roles = useRoles();
  const ventures = useVentures();
  const departments = useDepartments();
  const save = useSetUserRoles(user.id);
  const showToast = useToasts((state) => state.show);

  const [drafts, setDrafts] = useState<readonly Draft[]>(() => toDrafts(user));

  const roleOptions = (roles.data ?? [])
    .filter((role) => role.key !== 'candidate')
    .map((role) => ({
      value: role.id,
      label: role.label,
      ...(role.description ? { description: role.description } : {}),
    }));

  const isComplete = drafts.every(
    (draft) =>
      draft.roleId !== null &&
      (draft.scopeKind !== 'venture' || draft.ventureIds.length > 0) &&
      (draft.scopeKind !== 'department' || draft.departmentIds.length > 0),
  );
  const conflicts = conflictMessages(drafts);

  const patch = (key: string, next: Partial<Draft>): void => {
    setDrafts((current) =>
      current.map((draft) => (draft.key === key ? { ...draft, ...next } : draft)),
    );
  };

  return (
    <Card
      title="Roles and reach"
      meta="A role without a reach is not a permission"
      action={
        canEdit ? (
          <div className={styles.actions}>
            <Button
              variant="quiet"
              size="sm"
              icon={Plus}
              onClick={() =>
                setDrafts((current) => [
                  ...current,
                  {
                    key: `draft-${(nextKey += 1)}`,
                    roleId: null,
                    scopeKind: 'venture',
                    ownership: 'all',
                    ventureIds: [],
                    departmentIds: [],
                  },
                ])
              }
            >
              Add a role
            </Button>
            <Button
              variant="solid"
              size="sm"
              disabled={!isComplete || drafts.length === 0 || conflicts.size > 0}
              isPending={save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    assignments: drafts.map((draft) => ({
                      roleId: draft.roleId as string,
                      scopeKind: draft.scopeKind,
                      ownership: draft.ownership,
                      ventureIds: draft.ventureIds,
                      departmentIds: draft.departmentIds,
                    })),
                  },
                  {
                    onSuccess: () => {
                      showToast({
                        tone: 'success',
                        message: 'Roles updated',
                        detail: 'They have been signed out, so their new permissions take effect.',
                      });
                    },
                    onError: (error) => showToast({ tone: 'danger', message: error.message }),
                  },
                )
              }
            >
              Save roles
            </Button>
          </div>
        ) : undefined
      }
    >
      {drafts.length === 0 && (
        <p className={styles.warning}>
          No roles, so this person can reach nothing at all. Add one before they try to sign in.
        </p>
      )}

      <ul className={styles.rows}>
        {drafts.map((draft) => (
          <li key={draft.key} className={styles.row}>
            <Field label="Role" error={conflicts.get(draft.key)} isRequired>
              {({ inputId, describedBy, isInvalid }) => (
                <Select
                  id={inputId}
                  describedBy={describedBy}
                  isInvalid={isInvalid}
                  label="Role"
                  placeholder="Choose a role"
                  value={draft.roleId}
                  disabled={!canEdit}
                  options={roleOptions}
                  onChange={(value) => patch(draft.key, { roleId: value })}
                />
              )}
            </Field>

            <Field label="Covering" isRequired>
              {({ inputId }) => (
                <Select
                  id={inputId}
                  label="Covering"
                  value={draft.scopeKind}
                  disabled={!canEdit}
                  options={ROLE_SCOPE_KINDS.map((kind) => ({
                    value: kind,
                    label: ROLE_SCOPE_KIND_LABELS[kind],
                  }))}
                  onChange={(value) =>
                    patch(draft.key, {
                      scopeKind: value as RoleScopeKind,
                      ventureIds: [],
                      departmentIds: [],
                    })
                  }
                />
              )}
            </Field>

            {draft.scopeKind === 'venture' && (
              <Field
                label="Which ventures"
                error={draft.ventureIds.length === 0 ? 'Pick at least one venture.' : undefined}
                isRequired
              >
                {({ inputId }) => (
                  <MultiSelect
                    id={inputId}
                    label="Which ventures"
                    placeholder="Choose ventures"
                    value={draft.ventureIds}
                    disabled={!canEdit}
                    options={(ventures.data ?? []).map((venture) => ({
                      value: venture.id,
                      label: venture.name,
                      description: venture.code,
                    }))}
                    onChange={(value) => patch(draft.key, { ventureIds: [...value] })}
                  />
                )}
              </Field>
            )}

            {draft.scopeKind === 'department' && (
              <Field
                label="Which departments"
                error={
                  draft.departmentIds.length === 0 ? 'Pick at least one department.' : undefined
                }
                isRequired
              >
                {({ inputId }) => (
                  <MultiSelect
                    id={inputId}
                    label="Which departments"
                    placeholder="Choose departments"
                    value={draft.departmentIds}
                    disabled={!canEdit}
                    options={(departments.data ?? []).map((department) => ({
                      value: department.id,
                      label: department.name,
                    }))}
                    onChange={(value) => patch(draft.key, { departmentIds: [...value] })}
                  />
                )}
              </Field>
            )}

            <Field label="They can see">
              {({ inputId }) => (
                <Select
                  id={inputId}
                  label="They can see"
                  value={draft.ownership}
                  disabled={!canEdit}
                  options={ROLE_OWNERSHIPS.map((ownership) => ({
                    value: ownership,
                    label: ROLE_OWNERSHIP_LABELS[ownership],
                  }))}
                  onChange={(value) => patch(draft.key, { ownership: value as RoleOwnership })}
                />
              )}
            </Field>

            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                icon={Trash2}
                aria-label="Remove this role"
                onClick={() =>
                  setDrafts((current) => current.filter((item) => item.key !== draft.key))
                }
              />
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Security audit item 13: the row that repeats a role for a place it already reaches, keyed by draft,
 * with the plain-words reason. The server refuses the same rows (`roleAssignmentConflicts`).
 * A row with no role yet cannot repeat anything, so its key stands in for the role.
 */
function conflictMessages(drafts: readonly Draft[]): ReadonlyMap<string, string> {
  const conflicts = roleAssignmentConflicts(
    drafts.map((draft) => ({ ...draft, roleId: draft.roleId ?? draft.key })),
  );
  return new Map(
    conflicts.flatMap((conflict) => {
      const draft = drafts[conflict.index];
      return draft ? [[draft.key, ROLE_ASSIGNMENT_CONFLICT_MESSAGES[conflict.kind]] as const] : [];
    }),
  );
}

/**
 * The server stores one row per venture; the screen shows one row per role and scope.
 *
 * Two rows for "Recruiter, Venture B and C" is the right storage shape — it makes
 * the scope filter a plain `IN` and lets one venture be revoked on its own — but it
 * is the wrong editing shape, so they are folded back together here. The fold key
 * matches the permission resolver's grants (role, scope kind, ownership), so a
 * Recruiter for the whole organisation and for one venture stays two rows. A venture
 * stored twice folds to one, so saving the screen cleans the duplicate away.
 */
function toDrafts(user: UserDetail): readonly Draft[] {
  const byRole = new Map<string, Draft>();

  for (const assignment of user.assignments) {
    const foldKey = `${assignment.roleId}|${assignment.scopeKind}|${assignment.ownership}`;
    const existing = byRole.get(foldKey);
    if (existing) {
      if (assignment.ventureId && !existing.ventureIds.includes(assignment.ventureId)) {
        existing.ventureIds.push(assignment.ventureId);
      }
      if (assignment.departmentId && !existing.departmentIds.includes(assignment.departmentId)) {
        existing.departmentIds.push(assignment.departmentId);
      }
      continue;
    }
    byRole.set(foldKey, {
      key: `existing-${foldKey}`,
      roleId: assignment.roleId,
      scopeKind: assignment.scopeKind,
      ownership: assignment.ownership,
      ventureIds: assignment.ventureId ? [assignment.ventureId] : [],
      departmentIds: assignment.departmentId ? [assignment.departmentId] : [],
    });
  }

  return [...byRole.values()];
}
